import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { sendSignInCode, signInWithGoogle, verifySignInCode } from '@/api/session';
import { ApiError } from '@/lib/api';
import { Button } from '@m/ui/Controls';

function messageOf(err: unknown, fallback: string) {
  if (err instanceof ApiError && err.status === 429) return 'Too many tries. Wait a minute and try again.';
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * S15. Google, or a code sent to your email. Inside the iOS shell the Google
 * button never reaches the web: the shell intercepts /api/auth/sign-in/google
 * and runs the system sign-in sheet instead.
 */
export function SignInScreen() {
  const [params] = useSearchParams();
  const redirect = params.get('redirect')?.startsWith('/') ? params.get('redirect')! : '/time';
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'start' | 'email' | 'code'>(params.get('method') === 'email' ? 'email' : 'start');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(messageOf(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (e?: FormEvent) => {
    e?.preventDefault();
    void run(async () => {
      await sendSignInCode(email.trim());
      setCode('');
      setStep('code');
    }, 'Could not send a code.');
  };

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await verifySignInCode(email.trim(), code.trim());
      window.location.replace(redirect);
    }, 'That code did not work.');
  };

  const field =
    'h-[52px] w-full rounded-xl bg-[var(--page)] px-4 text-body outline-none ring-1 ring-[var(--line)] focus:ring-2 focus:ring-[var(--accent)]';

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-white px-6 pt-[calc(var(--sat)+24px)] pb-[calc(var(--sab)+24px)] dark:bg-[var(--page)]">
      <div className="flex flex-1 flex-col items-center justify-center gap-[18px] text-center">
        <span className="flex size-16 items-center justify-center rounded-[18px] bg-[var(--primary)] text-[30px] font-semibold tracking-[-0.04em] text-[var(--primary-fg)]">
          b
        </span>
        <span className="text-[32px] leading-[38px] font-semibold tracking-[-0.02em]">Billable</span>
        <span className="max-w-[280px] text-body leading-[1.45] text-[var(--text-muted)]">
          {step === 'code' ? (
            <>
              We sent a 6-digit code to <span className="text-[var(--text)]">{email}</span>.
            </>
          ) : (
            'Log your hours and send the invoice from your phone.'
          )}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {error && <p className="pb-1 text-center text-secondary text-[var(--danger)]">{error}</p>}

        {step === 'start' && (
          <>
            <Button variant="dark" className="h-[52px] gap-2.5" onClick={() => signInWithGoogle(redirect)}>
              <span className="flex size-[22px] items-center justify-center rounded-full bg-[var(--primary-fg)] text-[14px] font-bold text-[var(--primary)]">
                G
              </span>
              Continue with Google
            </Button>
            <Button variant="plain" className="h-12" onClick={() => setStep('email')}>
              Sign in with an email code instead
            </Button>
          </>
        )}

        {step === 'email' && (
          <form onSubmit={sendCode} className="flex flex-col gap-2">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoFocus
              placeholder="you@firm.com"
              aria-label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={field}
            />
            <Button type="submit" variant="dark" className="h-[52px]" disabled={busy || !email.includes('@')}>
              {busy ? 'Sending…' : 'Email me a code'}
            </Button>
            <Button variant="plain" className="h-12" onClick={() => setStep('start')}>
              Use Google instead
            </Button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={verify} className="flex flex-col gap-2">
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              placeholder="123456"
              aria-label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={`${field} text-center text-title tracking-[0.4em]`}
            />
            <Button type="submit" variant="dark" className="h-[52px]" disabled={busy || code.length !== 6}>
              {busy ? 'Checking…' : 'Sign in'}
            </Button>
            <Button variant="plain" className="h-12" disabled={busy} onClick={() => sendCode()}>
              Send a new code
            </Button>
          </form>
        )}

        <span className="mt-1 text-center text-caption leading-[1.5] text-[var(--text-subtle)]">
          By continuing you agree to the{' '}
          <a href="https://billable.to/terms" target="_blank" rel="noreferrer" className="text-[var(--text-muted)] underline">
            Terms
          </a>{' '}
          and{' '}
          <a href="https://billable.to/privacy" target="_blank" rel="noreferrer" className="text-[var(--text-muted)] underline">
            Privacy Policy
          </a>
          .
        </span>
      </div>
    </div>
  );
}
